/* eslint-disable react/no-array-index-key */
// Index keys are intentional here: rows are positional final-form paths
// (tiers[i].threshold), so the key must match the path index. Rows are only
// appended/removed, never reordered.
import React from "react";
import { Field } from "react-final-form";
import { ExlyDropdown, ExlyInput } from "common/form";
import { AddTierButton, TierDeleteButton } from "./OfferTierActions";
import { ArrayLevelError } from "./ArrayLevelError";
import { addRow, asArray, emptyTier, removeRowAt } from "../utils/tierOps";
import {
  ARRAY_ERROR_KEYS,
  DEFAULT_THRESHOLD_UNIT,
  DEFAULT_VALUE_UNIT,
  LTO_FORM_KEYS,
  THRESHOLD_UNIT,
  VALUE_UNIT,
} from "../constants";
import { OFFER_DIRECTION } from "../utils/unifiedOffer";
import { validateFlatFinalPrice } from "../limitedOfferValidators";

/**
 * Generic threshold tier builder (§4.3). Table-wide unit toggles, never per-row mix.
 * structuralDisabled locks shape (units, add/remove) on edit; disabled locks values.
 */
const VALUE_UNIT_OPTIONS = [
  { label: "%", value: VALUE_UNIT.PERCENT },
  { label: "₹ Flat", value: VALUE_UNIT.FLAT },
];

const TierBuilderTable = ({
  fieldPrefix = LTO_FORM_KEYS.TIERS,
  values = {},
  form,
  metricLabel = "THRESHOLD",
  thresholdPhrasing = "reaches or exceeds",
  numberSuffix = "#",
  allowPercentThreshold = true,
  showInternational = false,
  disabled = false,
  structuralDisabled = false,
  originalPrice = null,
}) => {
  const tiers = asArray(values[fieldPrefix]);
  const thresholdUnit = values.threshold_unit || DEFAULT_THRESHOLD_UNIT;
  const valueUnit = values.value_unit || DEFAULT_VALUE_UNIT;
  // Direction lives in form values (no new prop): surge raises prices,
  // Early Bird lowers them — header mirrors the direction.
  const isSurge = values[LTO_FORM_KEYS.DIRECTION] === OFFER_DIRECTION.SURGE;
  const validateValue = (value) =>
    valueUnit === VALUE_UNIT.FLAT
      ? validateFlatFinalPrice(value, originalPrice, { isSurge })
      : value === "" || value === null || value === undefined
      ? "Required"
      : undefined;

  const thresholdUnitOptions = [
    { label: "%", value: THRESHOLD_UNIT.PERCENT },
    { label: numberSuffix || "#", value: THRESHOLD_UNIT.COUNT },
  ];

  // Read the latest form state (not the render closure) so rapid clicks never
  // overwrite each other, and coerce to an array defensively.
  const currentTiers = () => asArray(form?.getState?.().values?.[fieldPrefix]);
  const addTier = () =>
    form.change(fieldPrefix, addRow(currentTiers(), emptyTier));
  const removeTier = (idx) =>
    form.change(fieldPrefix, removeRowAt(currentTiers(), idx));

  const gridCols = showInternational
    ? "tw-grid-cols-[1fr_1fr_1fr_32px]"
    : "tw-grid-cols-[1fr_1fr_32px]";

  return (
    <div className="tw-w-full">
      <span className="tw-block tw-text-[13px] tw-font-semibold tw-mb-2">
        Price Tiers *
      </span>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-2">
        {allowPercentThreshold ? (
          <>
            <span className="tw-text-[12px] tw-font-semibold tw-text-gray-500">
              Enter thresholds as
            </span>
            <Field
              name={LTO_FORM_KEYS.THRESHOLD_UNIT}
              component={ExlyDropdown}
              options={thresholdUnitOptions}
              disabled={disabled || structuralDisabled}
              size="small"
            />
          </>
        ) : (
          <span className="tw-text-[12px] tw-text-gray-500">
            Thresholds are absolute numbers
          </span>
        )}
        {/* value_unit is driven by Offer Type (Amount → ₹ Flat, Percentage → %).
            Kept as a hidden Field so the backend payload (threshold_unit,
            value_unit, tiers) stays exactly the same — no UI to change it. */}
        <div style={{ display: "none" }}>
          <Field
            name={LTO_FORM_KEYS.VALUE_UNIT}
            component={ExlyDropdown}
            options={VALUE_UNIT_OPTIONS}
            disabled={disabled || structuralDisabled}
            size="small"
          />
        </div>
      </div>

      {/* Table: one bordered container with a header row + divided rows. */}
      <div className="tw-rounded-[10px] tw-border tw-border-solid tw-border-[#E2E4EC] tw-overflow-hidden">
        <div
          className={`tw-grid ${gridCols} tw-gap-2.5 tw-border-0 tw-bg-[#F7F7FB] tw-px-3.5 tw-py-2.5 tw-text-[11px] tw-font-bold tw-text-[#6B7280]`}
        >
          <span>
            WHEN {metricLabel} ({thresholdPhrasing}
            {thresholdUnit === THRESHOLD_UNIT.PERCENT
              ? " %)"
              : ` ${numberSuffix || "#"})`}
          </span>
          <span>
            {isSurge ? "INCREASE" : "DECREASE"} PRICE{" "}
            {valueUnit === VALUE_UNIT.PERCENT ? "BY (%)" : "TO (₹)"}
          </span>
          {showInternational ? <span>INTERNATIONAL ($)</span> : null}
          <span />
        </div>
        {tiers.map((tier, idx) => (
          // Key is index-only (never the threshold value): folding the live
          // input value into the key remounts the row on every keystroke and
          // drops focus. Rows are positional final-form paths, never reordered.
          <div
            key={`${fieldPrefix}-${idx}`}
            className={`tw-grid ${gridCols} tw-items-center tw-gap-2.5 tw-mx-3.5 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-[#E2E4EC] tw-py-2.5`}
          >
            <Field
              name={`${fieldPrefix}[${idx}].threshold`}
              component={ExlyInput}
              type="number"
              min="0"
              placeholder={
                thresholdUnit === THRESHOLD_UNIT.PERCENT ? "e.g. 80" : "e.g. 18"
              }
              fullWidth
              disabled={disabled}
            />
            <Field
              name={`${fieldPrefix}[${idx}].value`}
              component={ExlyInput}
              type="number"
              min="0"
              placeholder={
                valueUnit === VALUE_UNIT.PERCENT ? "e.g. 20" : "e.g. 1200"
              }
              fullWidth
              disabled={disabled}
              validate={validateValue}
            />
            {showInternational ? (
              <Field
                name={`${fieldPrefix}[${idx}].value_international`}
                component={ExlyInput}
                type="number"
                min="0"
                placeholder="e.g. 20"
                fullWidth
                disabled={disabled}
              />
            ) : null}
            <TierDeleteButton
              disabled={disabled || structuralDisabled || tiers.length <= 1}
              onClick={() => removeTier(idx)}
              label="Remove tier"
            />
          </div>
        ))}
      </div>

      <AddTierButton
        disabled={disabled || structuralDisabled}
        onClick={addTier}
      >
        + Add tier
      </AddTierButton>
      <ArrayLevelError errorKey={ARRAY_ERROR_KEYS.TIERS} />
    </div>
  );
};

export { TierBuilderTable };
